import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLoop } from "@/pages/home/LoopContext";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const rituals = [
  {
    id: "celestia",
    name: "Celestia",
    subtitle: "天界の北極星",
    desc: "星の記憶を宿す",
    accent: "#7eb8d4",
    price: "¥88,000",
    paymentUrl: "https://square.link/u/f5jXSRx8",
    image: "https://public.readdy.ai/ai/img_res/520328b8ab5702c49740496dffca5237.jpg",
  },
  {
    id: "karmalaya",
    name: "Karmalaya",
    subtitle: "カルマの浄化",
    desc: "過去を手放す儀式",
    accent: "#d4a0b0",
    price: "¥66,000",
    paymentUrl: "https://square.link/u/dyI7TJoz",
    image: "https://readdy.ai/api/search-image?query=rose%20quartz%20lotus%20carved%20necklace%20pendant%20on%20dark%20background%2C%20soft%20pink%20crystal%20with%20golden%20setting%2C%20luxury%20spiritual%20jewelry%20macro%20photography%2C%20cinematic%20warm%20lighting%2C%20minimal%20dark%20background%2C%20mystical%20atmosphere&width=600&height=700&seq=prod002&orientation=portrait",
  },
  {
    id: "mandara",
    name: "Mandara",
    subtitle: "豊かさの炎",
    desc: "意図を形にする力",
    accent: "#d4a84c",
    price: "¥110,000",
    paymentUrl: "https://square.link/u/lYl6LYWY",
    image: "https://readdy.ai/api/search-image?query=golden%20mandala%20citrine%20ring%20on%20dark%20background%2C%20yellow%20crystal%20gemstone%20with%20intricate%20gold%20setting%2C%20luxury%20spiritual%20jewelry%20macro%20photography%2C%20cinematic%20warm%20amber%20lighting%2C%20minimal%20dark%20background%2C%20sacred%20geometry&width=600&height=700&seq=prod003&orientation=portrait",
  },
  {
    id: "luminara",
    name: "Luminara",
    subtitle: "直観の覚醒",
    desc: "見えないものを見る",
    accent: "#a07cc5",
    price: "¥77,000",
    paymentUrl: "https://square.link/u/ESY1WtmA",
    image: "https://readdy.ai/api/search-image?query=amethyst%20purple%20crystal%20earrings%20on%20dark%20background%2C%20violet%20gemstone%20with%20golden%20setting%2C%20luxury%20spiritual%20jewelry%20macro%20photography%2C%20cinematic%20purple%20glow%20lighting%2C%20minimal%20dark%20background%2C%20mystical%20aura&width=600&height=700&seq=prod004&orientation=portrait",
  },
  {
    id: "crystara",
    name: "Crystara Codex",
    subtitle: "魂の図書館",
    desc: "すべての記録を宿す",
    accent: "#c8d8e0",
    price: "¥99,000",
    paymentUrl: "https://square.link/u/JlXGtiyh",
    image: "https://readdy.ai/api/search-image?query=clear%20quartz%20crystal%20cuff%20bracelet%20on%20dark%20background%2C%20transparent%20crystal%20with%20silver%20gold%20setting%2C%20luxury%20spiritual%20jewelry%20macro%20photography%2C%20cinematic%20white%20light%20refraction%2C%20minimal%20dark%20background%2C%20sacred%20crystal%20energy&width=600&height=700&seq=prod005&orientation=portrait",
  },
];

function hexToRgbStr(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "201,168,76";
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

// ── 鍵アイコン（SVG不使用・CSS描画）──
function LockIcon({ unlocked, accent, animState }: {
  unlocked: boolean;
  accent: string;
  animState: "idle" | "denied" | "opening" | "open";
}) {
  const shakeStyle = animState === "denied"
    ? { animation: "lockShake 0.5s ease" }
    : {};
  const openStyle = animState === "opening" || animState === "open"
    ? { animation: "lockOpen 0.6s ease forwards" }
    : {};

  return (
    <div
      className="relative flex flex-col items-center"
      style={{ ...shakeStyle, ...openStyle }}
    >
      {/* 鍵の弓部分 */}
      <div
        className="w-4 h-3 rounded-t-full border-2 mb-0 transition-all duration-500"
        style={{
          borderColor: animState === "denied"
            ? "rgba(180,40,40,0.8)"
            : animState === "opening" || animState === "open"
            ? `${accent}`
            : `${accent}70`,
          borderBottom: "none",
          transform: animState === "opening" || animState === "open"
            ? "translateY(-3px) rotate(-30deg)"
            : "none",
          transformOrigin: "bottom right",
          transition: "transform 0.5s ease, border-color 0.3s ease",
        }}
      />
      {/* 鍵の本体 */}
      <div
        className="w-5 h-4 flex items-center justify-center rounded-sm"
        style={{
          background: animState === "denied"
            ? "rgba(120,10,10,0.6)"
            : animState === "opening" || animState === "open"
            ? `${accent}30`
            : `${accent}15`,
          border: `1px solid ${animState === "denied" ? "rgba(180,40,40,0.5)" : `${accent}40`}`,
          transition: "background 0.3s ease, border-color 0.3s ease",
        }}
      >
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{
            background: animState === "denied"
              ? "rgba(180,40,40,0.8)"
              : animState === "opening" || animState === "open"
              ? accent
              : `${accent}60`,
            transition: "background 0.3s ease",
          }}
        />
      </div>
    </div>
  );
}

// ── 拒絶オーバーレイ ──
function DeniedOverlay({ visible, onDone }: { visible: boolean; onDone: () => void }) {
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [visible, onDone]);

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center"
      style={{
        background: "rgba(6,5,10,0.92)",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity 0.3s ease",
      }}
    >
      <div
        className="text-center px-4"
        style={{ animation: visible ? "fadeInUp 0.4s ease" : "none" }}
      >
        {/* 閉じた鍵アイコン（大） */}
        <div className="flex justify-center mb-4">
          <div className="flex flex-col items-center" style={{ animation: "lockShake 0.5s ease 0.1s" }}>
            <div
              className="w-5 h-4 rounded-t-full border-2 mb-0"
              style={{ borderColor: "rgba(160,30,30,0.7)", borderBottom: "none" }}
            />
            <div
              className="w-7 h-5 flex items-center justify-center rounded-sm"
              style={{ background: "rgba(80,10,10,0.6)", border: "1px solid rgba(160,30,30,0.4)" }}
            >
              <div className="w-2 h-2 rounded-full" style={{ background: "rgba(180,40,40,0.8)" }} />
            </div>
          </div>
        </div>

        <p
          className="text-xs tracking-[0.3em] mb-2"
          style={{ color: "rgba(160,40,40,0.8)", fontFamily: "'Cormorant Garamond', serif" }}
        >
          扉は、拒んでいる
        </p>
        <p
          className="text-xs tracking-[0.2em]"
          style={{ color: "rgba(255,255,255,0.15)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
        >
          奈落を越えた者のみ、手にできる
        </p>
      </div>
    </div>
  );
}

// ── 開錠オーバーレイ ──
function UnlockedOverlay({ visible, accent, paymentUrl, name }: {
  visible: boolean;
  accent: string;
  paymentUrl: string;
  name: string;
}) {
  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center"
      style={{
        background: `rgba(6,5,10,0.88)`,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity 0.5s ease",
      }}
    >
      {visible && (
        <div
          className="text-center px-4"
          style={{ animation: "fadeInUp 0.5s ease" }}
        >
          {/* 開いた鍵（大） */}
          <div className="flex justify-center mb-4">
            <div className="flex flex-col items-center">
              <div
                className="w-5 h-4 rounded-t-full border-2 mb-0"
                style={{
                  borderColor: accent,
                  borderBottom: "none",
                  transform: "translateY(-3px) rotate(-35deg)",
                  transformOrigin: "bottom right",
                  boxShadow: `0 0 8px ${accent}60`,
                }}
              />
              <div
                className="w-7 h-5 flex items-center justify-center rounded-sm"
                style={{
                  background: `${accent}20`,
                  border: `1px solid ${accent}60`,
                  boxShadow: `0 0 12px ${accent}30`,
                }}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ background: accent, boxShadow: `0 0 6px ${accent}` }}
                />
              </div>
            </div>
          </div>

          <p
            className="text-xs tracking-[0.3em] mb-1"
            style={{ color: `${accent}`, fontFamily: "'Cormorant Garamond', serif" }}
          >
            扉が、開いた
          </p>
          <p
            className="text-xs tracking-[0.2em] mb-5"
            style={{ color: "rgba(255,255,255,0.2)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
          >
            {name} を手に入れる
          </p>

          <a
            href={paymentUrl}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-block text-xs tracking-[0.4em] uppercase transition-all duration-500 cursor-pointer whitespace-nowrap"
            style={{
              border: `1px solid ${accent}50`,
              padding: "8px 20px",
              color: `${accent}`,
              fontFamily: "'Cormorant Garamond', serif",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = `${accent}15`;
              (e.currentTarget as HTMLElement).style.borderColor = `${accent}90`;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "transparent";
              (e.currentTarget as HTMLElement).style.borderColor = `${accent}50`;
            }}
          >
            ACQUIRE
          </a>
        </div>
      )}
    </div>
  );
}

// ループ段階別のGatewayコンテンツ
function getGatewayContent(loopCount: number, isUnlocked: boolean) {
  if (isUnlocked) {
    return {
      label: "Sacred Objects",
      title: "儀式への入口",
      sub: "これは商品ではない。",
      lockedHint: null,
      accentColor: "rgba(255,220,140,",
    };
  }
  if (loopCount === 0) {
    return {
      label: "Sacred Objects",
      title: "儀式への入口",
      sub: "これは商品ではない。",
      lockedHint: "— 扉は、まだ閉じている —",
      accentColor: "rgba(201,168,76,",
    };
  }
  if (loopCount <= 2) {
    return {
      label: "Sacred Objects",
      title: "螺旋の先にある",
      sub: "招待コードを持つ者だけが、触れることができる。",
      lockedHint: `— ${loopCount}度の螺旋を経て、まだ扉は閉じている —`,
      accentColor: "rgba(201,168,76,",
    };
  }
  if (loopCount <= 4) {
    return {
      label: "Sealed Objects",
      title: "封印された王国",
      sub: "宇宙の意思が、あなたを導いている。",
      lockedHint: `— ${loopCount}度の試み。扉は、あなたを知っている —`,
      accentColor: "rgba(210,130,50,",
    };
  }
  // 5回以上（奈落直前）
  return {
    label: "Forbidden Objects",
    title: "奈落の向こうに",
    sub: "次の螺旋で、すべてが変わる。",
    lockedHint: `— ${loopCount}度の螺旋。次が、最後の扉だ —`,
    accentColor: "rgba(200,70,30,",
  };
}

export default function RitualGateway() {
  const { ref, inView } = useInView(0.1);
  const { loopCount, isUnlocked: isVaultUnlocked } = useLoop();
  const gatewayContent = getGatewayContent(loopCount, isVaultUnlocked);
  const isNearAbyss = loopCount >= 4 && !isVaultUnlocked;

  const [hovered, setHovered] = useState<number | null>(null);
  const [lockStates, setLockStates] = useState<("idle" | "denied" | "opening" | "open")[]>(
    rituals.map(() => "idle")
  );
  const [showDenied, setShowDenied] = useState<boolean[]>(rituals.map(() => false));
  const [showUnlocked, setShowUnlocked] = useState<boolean[]>(rituals.map(() => false));

  const memberName = sessionStorage.getItem("vault_member_name");
  const memberTier = sessionStorage.getItem("vault_member_tier");

  const handlePurchaseClick = useCallback((e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (isVaultUnlocked) {
      // 開錠アニメーション
      setLockStates((prev) => prev.map((s, i) => i === idx ? "opening" : s));
      setTimeout(() => {
        setLockStates((prev) => prev.map((s, i) => i === idx ? "open" : s));
        setShowUnlocked((prev) => prev.map((v, i) => i === idx ? true : v));
      }, 600);
    } else {
      // 拒絶アニメーション
      setLockStates((prev) => prev.map((s, i) => i === idx ? "denied" : s));
      setShowDenied((prev) => prev.map((v, i) => i === idx ? true : v));
      setTimeout(() => {
        setLockStates((prev) => prev.map((s, i) => i === idx ? "idle" : s));
      }, 600);
    }
  }, [isVaultUnlocked]);

  const handleDeniedDone = useCallback((idx: number) => {
    setShowDenied((prev) => prev.map((v, i) => i === idx ? false : v));
  }, []);

  const handleCloseUnlocked = useCallback((idx: number) => {
    setShowUnlocked((prev) => prev.map((v, i) => i === idx ? false : v));
    setLockStates((prev) => prev.map((s, i) => i === idx ? "idle" : s));
  }, []);

  return (
    <section className="relative bg-[#080608] py-20 md:py-40 px-4 md:px-6 overflow-hidden">
      <div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-24"
        style={{ background: `linear-gradient(to bottom, transparent, ${gatewayContent.accentColor}0.2)` }}
      />

      {/* 深淵近く：赤いオーバーレイ */}
      {isNearAbyss && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 50% 0%, rgba(40,0,0,0.2) 0%, transparent 60%)",
          }}
        />
      )}

      <div ref={ref} className="max-w-6xl mx-auto">
        <div
          className="text-center mb-12 md:mb-24"
          style={{ opacity: inView ? 1 : 0, transition: "opacity 1.5s ease" }}
        >
          <div
            className="text-xs tracking-[0.4em] md:tracking-[0.5em] uppercase mb-4 md:mb-6"
            style={{ color: `${gatewayContent.accentColor}0.4)`, transition: "color 1.5s ease" }}
          >
            {gatewayContent.label}
          </div>
          <h2
            className="text-2xl md:text-4xl font-light mb-4"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: isNearAbyss ? "rgba(220,100,60,0.8)" : "rgba(255,255,255,0.8)",
              transition: "color 1.5s ease",
            }}
          >
            {gatewayContent.title}
          </h2>
          <p className="text-white/20 text-xs md:text-sm tracking-[0.2em]">
            これは商品ではない。
          </p>

          {/* 解錠済みバッジ */}
          {isVaultUnlocked && (
            <div
              className="flex flex-col items-center gap-3 mt-6 md:mt-8"
              style={{ animation: "fadeInUp 1s ease 0.3s both" }}
            >
              <div
                className="inline-flex items-center gap-2 md:gap-3 px-3 md:px-6 py-2 md:py-2.5"
                style={{
                  border: "1px solid rgba(255,220,140,0.3)",
                  background: "rgba(255,220,140,0.04)",
                  backdropFilter: "blur(8px)",
                  animation: "covenantGlow 4s ease-in-out infinite",
                }}
              >
                <div className="flex flex-col items-center" style={{ width: "16px" }}>
                  <div
                    style={{
                      width: "10px",
                      height: "8px",
                      borderRadius: "5px 5px 0 0",
                      border: "1px solid rgba(255,220,140,0.7)",
                      borderBottom: "none",
                      transform: "translateY(-2px) rotate(-35deg)",
                      transformOrigin: "bottom right",
                      boxShadow: "0 0 6px rgba(255,220,140,0.4)",
                    }}
                  />
                  <div
                    style={{
                      width: "13px",
                      height: "9px",
                      borderRadius: "2px",
                      background: "rgba(255,220,140,0.12)",
                      border: "1px solid rgba(255,220,140,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 0 8px rgba(255,220,140,0.2)",
                    }}
                  >
                    <div
                      style={{
                        width: "4px",
                        height: "4px",
                        borderRadius: "50%",
                        background: "rgba(255,220,140,0.9)",
                        boxShadow: "0 0 6px rgba(255,220,140,0.8)",
                      }}
                    />
                  </div>
                </div>
                <div className="flex flex-col items-start gap-0.5">
                  <span
                    className="text-xs tracking-[0.3em] md:tracking-[0.45em] uppercase"
                    style={{ color: "rgba(255,220,140,0.7)", fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    COVENANT SEALED
                  </span>
                  {memberName ? (
                    <span
                      className="text-xs tracking-[0.15em] md:tracking-[0.2em]"
                      style={{ color: "rgba(255,220,140,0.5)", fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {memberName}
                      {memberTier && (
                        <span style={{ color: "rgba(255,220,140,0.3)", marginLeft: "8px", letterSpacing: "0.3em", fontSize: "0.85em" }}>
                          {memberTier.toUpperCase()}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span
                      className="text-xs tracking-[0.15em] md:tracking-[0.2em]"
                      style={{ color: "rgba(255,220,140,0.35)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
                    >
                      神々の許しを得た者
                    </span>
                  )}
                </div>
              </div>

              {/* 天使の羽根の装飾ライン */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-px" style={{ background: "linear-gradient(to right, transparent, rgba(255,220,140,0.2))" }} />
                <div className="flex gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-1 h-1 rounded-full"
                      style={{
                        background: "rgba(255,220,140,0.3)",
                        animation: `dotFloat ${1.5 + i * 0.3}s ease-in-out infinite`,
                        animationDelay: `${i * 0.2}s`,
                      }}
                    />
                  ))}
                </div>
                <div className="w-12 h-px" style={{ background: "linear-gradient(to left, transparent, rgba(255,220,140,0.2))" }} />
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          {rituals.map((ritual, i) => (
            <div key={i} className="relative group">
              <div
                className="cursor-pointer block"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div
                  style={{
                    opacity: inView ? 1 : 0,
                    transform: inView ? "translateY(0)" : "translateY(24px)",
                    transition: `opacity 1.5s ease ${i * 0.12}s, transform 1.5s ease ${i * 0.12}s`,
                  }}
                >
                  {/* Image container */}
                  <div className="relative overflow-hidden mb-3 md:mb-4" style={{ paddingBottom: "120%" }}>
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
                      style={{
                        backgroundImage: `url(${ritual.image})`,
                        filter: hovered === i
                          ? isNearAbyss ? "brightness(0.55) saturate(0.6) hue-rotate(-5deg)" : "brightness(0.65) saturate(0.8)"
                          : isNearAbyss ? "brightness(0.3) saturate(0.2)" : "brightness(0.4) saturate(0.4)",
                        transform: hovered === i ? "scale(1.04)" : "scale(1)",
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#080608] via-transparent to-transparent" />

                    {/* Hover overlay（説明） */}
                    <div
                      className="absolute inset-0 items-end justify-center pb-14 transition-opacity duration-700 hidden md:flex"
                      style={{ opacity: hovered === i && !showDenied[i] && !showUnlocked[i] ? 1 : 0 }}
                    >
                      <div className="text-center px-3">
                        <div className="w-8 h-px mx-auto mb-3" style={{ background: `${ritual.accent}60` }} />
                        <p
                          className="text-white/60 text-xs tracking-[0.2em]"
                          style={{ fontFamily: "'Cormorant Garamond', serif" }}
                        >
                          {ritual.desc}
                        </p>
                      </div>
                    </div>

                    {/* ── 購入ボタン（鍵アイコン付き）── */}
                    <div
                      className="absolute bottom-2 md:bottom-3 left-0 right-0 flex justify-center transition-all duration-500"
                      style={{
                        opacity: hovered === i && !showDenied[i] && !showUnlocked[i] ? 1 : 0,
                        transform: hovered === i ? "translateY(0)" : "translateY(6px)",
                      }}
                    >
                      <button
                        onClick={(e) => handlePurchaseClick(e, i)}
                        className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 cursor-pointer whitespace-nowrap transition-all duration-300"
                        style={{
                          background: "rgba(6,5,10,0.7)",
                          border: `1px solid ${ritual.accent}30`,
                          backdropFilter: "blur(8px)",
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.borderColor = `${ritual.accent}60`;
                          (e.currentTarget as HTMLElement).style.background = `rgba(6,5,10,0.85)`;
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.borderColor = `${ritual.accent}30`;
                          (e.currentTarget as HTMLElement).style.background = `rgba(6,5,10,0.7)`;
                        }}
                      >
                        <LockIcon
                          unlocked={isVaultUnlocked}
                          accent={ritual.accent}
                          animState={lockStates[i]}
                        />
                        <span
                          className="text-xs tracking-[0.2em] md:tracking-[0.3em]"
                          style={{
                            color: isVaultUnlocked ? ritual.accent : `${ritual.accent}55`,
                            fontFamily: "'Cormorant Garamond', serif",
                          }}
                        >
                          {isVaultUnlocked ? "購入する" : "購入する"}
                        </span>
                      </button>
                    </div>

                    {/* 拒絶オーバーレイ */}
                    <DeniedOverlay
                      visible={showDenied[i]}
                      onDone={() => handleDeniedDone(i)}
                    />

                    {/* 開錠オーバーレイ */}
                    {showUnlocked[i] && (
                      <div onClick={() => handleCloseUnlocked(i)} className="absolute inset-0 z-20">
                        <UnlockedOverlay
                          visible={showUnlocked[i]}
                          accent={ritual.accent}
                          paymentUrl={ritual.paymentUrl}
                          name={ritual.name}
                        />
                      </div>
                    )}

                    {/* Glow border on hover */}
                    <div
                      className="absolute inset-0 pointer-events-none transition-opacity duration-700"
                      style={{
                        boxShadow: `inset 0 0 30px rgba(${hexToRgbStr(ritual.accent)},0.12)`,
                        border: `1px solid ${ritual.accent}${hovered === i ? "25" : "00"}`,
                        opacity: hovered === i ? 1 : 0,
                      }}
                    />
                  </div>

                  {/* Text */}
                  <div className="text-center">
                    <div
                      className="text-xs md:text-sm font-light tracking-[0.15em] md:tracking-[0.2em] mb-1 transition-colors duration-500"
                      style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        color: hovered === i ? ritual.accent : `${ritual.accent}70`,
                      }}
                    >
                      {ritual.name}
                    </div>
                    <div className="text-white/25 text-xs tracking-wider mb-1 hidden md:block">{ritual.subtitle}</div>
                    {/* 価格（解錠済みのみ表示） */}
                    {isVaultUnlocked && (
                      <div
                        className="text-xs tracking-wider"
                        style={{ color: `${ritual.accent}50`, fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {ritual.price}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 未解錠の場合のヒント */}
        {!isVaultUnlocked && gatewayContent.lockedHint && (
          <div
            className="text-center mt-10 md:mt-16"
            style={{ opacity: inView ? 1 : 0, transition: "opacity 2s ease 1s" }}
          >
            <p
              className="text-xs tracking-[0.3em]"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: "italic",
                color: isNearAbyss ? "rgba(180,50,10,0.25)" : "rgba(255,255,255,0.1)",
                transition: "color 1.5s ease",
              }}
            >
              {gatewayContent.lockedHint}
            </p>
          </div>
        )}

        {/* 解錠済みの場合のフッター */}
        {isVaultUnlocked && (
          <div
            className="text-center mt-10 md:mt-16"
            style={{ opacity: inView ? 1 : 0, transition: "opacity 2s ease 1s" }}
          >
            <div
              className="inline-block border border-[#c9a84c]/20 px-6 md:px-8 py-3 cursor-pointer hover:border-[#c9a84c]/40 transition-colors duration-700"
            >
              <Link
                to="/product/celestia"
                className="text-[#c9a84c]/50 text-xs tracking-[0.3em] md:tracking-[0.4em] uppercase hover:text-[#c9a84c]/70 transition-colors duration-500"
                style={{ textDecoration: "none" }}
              >
                すべての儀式を見る
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* アニメーション定義 */}
      <style>{`
        @keyframes lockShake {
          0%   { transform: translateX(0) rotate(0deg); }
          15%  { transform: translateX(-4px) rotate(-3deg); }
          30%  { transform: translateX(4px) rotate(3deg); }
          45%  { transform: translateX(-3px) rotate(-2deg); }
          60%  { transform: translateX(3px) rotate(2deg); }
          75%  { transform: translateX(-2px) rotate(-1deg); }
          100% { transform: translateX(0) rotate(0deg); }
        }
        @keyframes lockOpen {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes covenantGlow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(255,220,140,0.05), inset 0 0 15px rgba(255,220,140,0.02);
            border-color: rgba(255,220,140,0.25);
          }
          50% {
            box-shadow: 0 0 40px rgba(255,220,140,0.12), inset 0 0 25px rgba(255,220,140,0.06);
            border-color: rgba(255,220,140,0.45);
          }
        }
        @keyframes dotFloat {
          0%, 100% { transform: translateY(0); opacity: 0.3; }
          50%       { transform: translateY(-3px); opacity: 0.7; }
        }
      `}</style>
    </section>
  );
}
